import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { StockCard } from '../components/modern/StockCard';
import { LoadingState } from '../components/LoadingSpinner';
import { apiService } from '../services/api';
import { Recommendation, FilterOptions, SearchFilters } from '../types';
import { TrendingUp, TrendingDown, BarChart3, Filter, Grid, List } from 'lucide-react';

interface OutletContextType {
  filters: FilterOptions;
  searchQuery: string;
  activeTab: string;
}

export const Dashboard: React.FC = () => {
  const { filters, searchQuery, activeTab } = useOutletContext<OutletContextType>();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const navigate = useNavigate();

  // Fetch recommendations based on filters
  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;

      // Call different API methods based on activeTab
      switch (activeTab) {
        case 'new':
          response = await apiService.getNewRecommendations('all', { query: searchQuery || undefined, filters });
          break;
        case 'high-upside':
          response = await apiService.getHighUpsideRecommendations({ query: searchQuery || undefined, filters });
          break;
        case 'high-downside':
          response = await apiService.getHighDownsideRecommendations({ query: searchQuery || undefined, filters });
          break;
        default:
          // For 'all' and other tabs, use the general recommendations endpoint
          const searchFilters: SearchFilters = {
            query: searchQuery || undefined,
            filters,
            page: 1,
            limit: 100
          };
          response = await apiService.getRecommendations(searchFilters);
      }

      if (response.success) {
        // Map API response to match frontend expectations
        const mappedData = response.data.map((rec: any) => ({
          ...rec,
          stock: rec.company ? {
            id: rec.company.id,
            symbol: rec.company.seo_name || rec.company.name,
            name: rec.company.name,
            sector: rec.company.sector || 'Diversified',
            current_price: rec.current_price
          } : null,
          brokerage: rec.organization ? {
            id: rec.organization.id,
            name: rec.organization.name,
            slug: rec.organization.seo_name
          } : null,
          recommendation: rec.recommendation_type,
          potentialUpside: rec.upside_potential,
          potential_value: rec.upside_potential,
          targetPrice: rec.target_price,
          target_price: rec.target_price,
          stock_score: rec.stock_score,
          stock_score_data: rec.stock_score_data
        }));
        setRecommendations(mappedData);
      } else {
        setError(response.message || 'Failed to fetch recommendations');
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to fetch recommendations');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [filters, searchQuery, activeTab]);

  const stats = {
    total: recommendations.length,
    buy: recommendations.filter(r => r.recommendation === 'Buy').length,
    sell: recommendations.filter(r => r.recommendation === 'Sell').length,
    hold: recommendations.filter(r => r.recommendation === 'Hold').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-10 rounded-xl w-96"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-2xl"></div>)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="h-80 bg-gray-200 rounded-2xl"></div>)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-white text-white tracking-tight">
                {activeTab === 'new' ? 'New Recommendations' : 
                 activeTab === 'high-upside' ? 'High Upside Stocks' : 
                 activeTab === 'high-downside' ? 'High Downside Stocks' : 
                 'All Recommendations'}
              </h1>
            </div>
            <p className="text-gray-600 ml-14">
              {searchQuery ? `Search results for "${searchQuery}"` : 
               activeTab === 'new' ? 'Recently published stock recommendations' :
               activeTab === 'high-upside' ? 'Stocks with significant upside potential' :
               activeTab === 'high-downside' ? 'Stocks with significant downside risk' :
               'Latest stock recommendations from top brokerages'}
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-white rounded-xl p-1.5 border border-gray-200 shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'grid'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-blue-50">
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-green-50">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Buy</span>
            </div>
            <div className="text-3xl font-bold text-green-600">{stats.buy}</div>
            <div className="text-xs text-gray-500 mt-1">
              {((stats.buy / stats.total) * 100).toFixed(0)}% of total
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-red-50">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Sell</span>
            </div>
            <div className="text-3xl font-bold text-red-600">{stats.sell}</div>
            <div className="text-xs text-gray-500 mt-1">
              {((stats.sell / stats.total) * 100).toFixed(0)}% of total
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-amber-50">
                <Filter className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Hold</span>
            </div>
            <div className="text-3xl font-bold text-amber-600">{stats.hold}</div>
            <div className="text-xs text-gray-500 mt-1">
              {((stats.hold / stats.total) * 100).toFixed(0)}% of total
            </div>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">{error}</div>
            <button
              onClick={fetchRecommendations}
              className="mt-4 px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : recommendations.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
            <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <BarChart3 className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No recommendations found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your filters or search criteria</p>
            <button
              onClick={() => navigate('/overview')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Go to Overview
            </button>
          </div>
        ) : (
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5'
              : 'space-y-4'
          }>
            {recommendations.map((recommendation) => (
              <StockCard
                key={recommendation.id}
                recommendation={recommendation}
                variant={viewMode === 'list' ? 'compact' : 'featured'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};